import React, {useEffect, useState} from "react";
import axios from "axios";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from "recharts";
import {baseUrl} from "../Univ";
function ExecutiveDashboard() {
    const today = new Date().toISOString().substring(0, 10);
    // =========================
    // STATES
    // =========================
    const [opCount, setOpCount] = useState(0);
    const [ipCount, setIpCount] = useState(0);
    const [labCount, setLabCount] = useState(0);
    const [invCount, setInvCount] = useState(0);
    const [revenue, setRevenue] = useState(0);
    // FIXED MISSING STATE
    const [occupancy, setOccupancy] = useState({
        totalBeds: 0,
        occupied: 0,
        available: 0,
        icu: 0
    });
    const [revenueTrend, setRevenueTrend] = useState([]);
    const [wardData, setWardData] = useState([]);
    const [patientFlow, setPatientFlow] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const [criticalPatients, setCriticalPatients] = useState([]);
    const [occupancyPercent, setOccupancyPercent] = useState(0);
    const [revenuePercent, setRevenuePercent] = useState(0);
    const [icuPercent, setIcuPercent] = useState(0);
    const [metrics, setMetrics] = useState({
        totalBeds: 0,
        occupied: 0,
        available: 0,
        icu: 0,
        occupancyPercent: 0,
        revenuePercent: 0,
        icuPercent: 0,
        totalRevenue: 0
    });
    const COLORS = [
        "#00C49F",
        "#0088FE",
        "#FFBB28",
        "#FF8042",
        "#FF4444"
    ];
    // =========================
    // FETCH DATA
    // =========================
    async function fetchDashboardData() {
        try {
            console.log("DASHBOARD LOAD START");
            // ============================================
            // API CALLS
            // ============================================
            const [
                opRes,
                ipRes,
                labRes,
                invRes,
                collectionRes,
                ipCollectionRes,
                roomRes
            ] = await Promise.all([
                axios.get(baseUrl + "op-appointment-view/"),
                axios.get(`${baseUrl}view-ip/?status=2`),
                axios.get(`${baseUrl}lab-entry-view/${today}/`),
                axios.get(`${baseUrl}inv-entry-view/${today}/`),
                axios.get(
                    `${baseUrl}all-collection-view/?start_date=${today}&end_date=${today}&cashier=&mode=&status=2`
                ),
                axios.get(
                    `${baseUrl}all-ip-collection-view/?start_date=${today}&end_date=${today}&cashier=&mode=&status=2`
                ),
                axios.get(`${baseUrl}room-no/`)
            ]);
            // ============================================
            // TODAY OP
            // ============================================
            const todayOp = opRes.data.filter(
                r => r.appt_date === today
            );
            console.log("TODAY OP", todayOp);
            const opTodayCount = todayOp.length;
            setOpCount(opTodayCount);
            // ============================================
            // IP
            // ============================================
            const ipTodayCount = ipRes.data.length;
            console.log("IP COUNT", ipTodayCount);
            setIpCount(ipTodayCount);
            // ============================================
            // LAB
            // ============================================
            const totalLab = Array.isArray(labRes.data)
                ? labRes.data.length
                : 0;
            console.log("LAB COUNT", totalLab);
            setLabCount(totalLab);
            // ============================================
            // RADIOLOGY
            // ============================================
            const totalInv = Array.isArray(invRes.data)
                ? invRes.data.length
                : 0;
            console.log("INV COUNT", totalInv);
            setInvCount(totalInv);
            // ============================================
            // REVENUE
            // ============================================
            let revenueTotal = 0;
            let TotalCons = 0;
            let TotalLabAmount = 0;
            let TotalInvAmount = 0;
            let TotalMinor = 0;
            let TotalAdv = 0;
            let TotalIpLab = 0;
            let TotalIpInv = 0;
            // OP COLLECTION
            if (Array.isArray(collectionRes.data)) {
                collectionRes.data.forEach(p => {
                    const paid = Number(p.paid || 0);
                    const labPaid = Number(p.lab_paid || 0);
                    const invPaid = Number(p.inv_paid || 0);
                    const minPaid = Number(p.min_paid || 0);
                    revenueTotal += (
                        paid +
                        labPaid +
                        invPaid +
                        minPaid
                    );
                    TotalCons += paid;
                    TotalLabAmount += labPaid;
                    TotalInvAmount += invPaid;
                    TotalMinor += minPaid;
                });
            }
            // IP COLLECTION
            if (Array.isArray(ipCollectionRes.data)) {
                ipCollectionRes.data.forEach(p => {
                    const adv = Number(p.paid || 0);
                    const ipLab = Number(p.lab_paid || 0);
                    const ipInv = Number(p.inv_paid || 0);
                    revenueTotal += (
                        adv +
                        ipLab +
                        ipInv
                    );
                    TotalAdv += adv;
                    TotalIpLab += ipLab;
                    TotalIpInv += ipInv;
                });
            }
            console.log("TOTAL REVENUE", revenueTotal);
            setRevenue(revenueTotal);
            // ============================================
            // ROOM / OCCUPANCY
            // ============================================
            // ============================================
// ROOM / OCCUPANCY (FIXED)
// ============================================
            const rooms = roomRes.data || [];
            const totalBeds = rooms.length;
            const occupied = rooms.filter(r => Number(r.status) === 0).length;
            const available = rooms.filter(r => Number(r.status) === 1).length;
            const icu = rooms.filter(r => {
                const name = String(r.room || "").toLowerCase();
                return Number(r.status) === 0 && name.includes("icu");
            }).length;
// revenue already calculated above as revenueTotal
            const occupancyPercent = totalBeds > 0
                ? (occupied / totalBeds) * 100
                : 0;
            const revenuePercent = revenueTotal > 0
                ? (revenueTotal / 200000) * 100
                : 0;
            const icuPercent = 20 > 0
                ? (icu / 20) * 100
                : 0;
            setOccupancy({
                totalBeds,
                occupied,
                available,
                icu
            });
            setRevenue(revenueTotal);
            setMetrics({
                totalBeds,
                occupied,
                available,
                icu,
                occupancyPercent,
                revenuePercent,
                icuPercent,
                totalRevenue: revenueTotal
            });
            // ============================================
            // REVENUE TREND
            // ============================================
            const trendData = [
                {
                    time: "Consult",
                    amount: TotalCons
                },
                {
                    time: "Lab",
                    amount: TotalLabAmount + TotalIpLab
                },
                {
                    time: "Radiology",
                    amount: TotalInvAmount + TotalIpInv
                },
                {
                    time: "Minor",
                    amount: TotalMinor
                },
                {
                    time: "Advance",
                    amount: TotalAdv
                }
            ];
            console.log("TREND DATA", trendData);
            setRevenueTrend(trendData);
            // ============================================
            // WARD DATA
            // ============================================
            const wardMap = {};
            rooms.forEach(room => {
                const ward = String(
                    room.room ||
                    room.room_no ||
                    "Unknown"
                );
                if (!wardMap[ward]) {
                    wardMap[ward] = 0;
                }
                if (Number(room.status) === 0) {
                    wardMap[ward]++;
                }
            });
            const wardArray = Object.keys(wardMap).map(key => ({
                ward: key,
                occupied: wardMap[key]
            }));
            console.log("WARD ARRAY", wardArray);
            setWardData(wardArray);
            // ============================================
            // PATIENT FLOW
            // ============================================
            const flowData = [
                {
                    name: "OP",
                    value: opTodayCount
                },
                {
                    name: "IP",
                    value: ipTodayCount
                },
                {
                    name: "Lab",
                    value: totalLab
                },
                {
                    name: "Radiology",
                    value: totalInv
                }
            ];
            setPatientFlow(flowData);
        } catch (err) {
            console.log(err);
            if (err.response) {
                console.log("ERROR RESPONSE", err.response.data);
            }
        }
    }
// =====================
// SAFE GAUGE DATA
// =====================
    const occupancyGauge = [
        {
            name: "Occupancy",
            value: metrics.occupancyPercent,
            fill: "#ff4d4f"
        }
    ];
    const revenueGauge = [
        {
            name: "Revenue",
            value: metrics.revenuePercent,
            fill: "#00C49F"
        }
    ];
    const icuGauge = [
        {
            name: "ICU",
            value: metrics.icuPercent,
            fill: "#fbbf24"
        }
    ];
// =====================================
// PHARMACY STATES
// =====================================
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [rows, setRows] = useState([]);
    const [combinedData, setcombinedData] = useState([]);
    const [viewPharmData, setViewPharmData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    // ---------------- FETCH SALES ----------------
    async function fetchData() {
        setIsLoading(true);
        const start = startDate ? startDate.toISOString().substring(0, 10) : "";
        const end = endDate ? endDate.toISOString().substring(0, 10) : "";
        try {
            const res = await axios.get(
                `${baseUrl}all-pharm-sale/?start_date=${start}&end_date=${end}&status=1`
            );
            setcombinedData(res.data);
            setIsLoading(false);
        } catch (err) {
            console.log(err);
            setIsLoading(false);
        }
    }
    useEffect(() => {
        fetchData();
    }, [startDate, endDate]);
    // ---------------- FETCH PHARMACY DATA ----------------
    async function fetchPharmData() {
        try {
            const productIds = combinedData.reduce((ids, row) => {
                const products = (row.product || "")
                    .toString()
                    .split(",")
                    .map(i => i.trim())
                    .filter(Boolean);
                return [...ids, ...products];
            }, []);
            const uniqueIds = [...new Set(productIds)];
            const indentResponses = await Promise.all(
                uniqueIds.map(id =>
                    axios.get(`${baseUrl}update-indent-use/${id}/`)
                )
            );
            const saleToPharmaMap = {};
            indentResponses.forEach(res => {
                saleToPharmaMap[res.data.id] = res.data.product;
            });
            const pharmaIds = [...new Set(Object.values(saleToPharmaMap))];
            const pharmResponses = await Promise.all(
                pharmaIds.map(id =>
                    axios.get(`${baseUrl}update-pharm-qty/${id}/`)
                )
            );
            const pharmaMap = {};
            pharmResponses.forEach(res => {
                pharmaMap[res.data.id] = res.data;
            });
            const updated = combinedData.map(row => {
                const ids = (row.product || "").split(",");
                const qtys = (row.qty || "").split(",");
                return {
                    ...row,
                    enriched: ids.map((saleId, i) => {
                        const pharmaId = saleToPharmaMap[saleId];
                        return {
                            qty: parseFloat(qtys[i] || 0),
                            data: pharmaMap[pharmaId] || null
                        };
                    })
                };
            });
            setRows(updated);
            setViewPharmData(pharmaMap);
        } catch (err) {
            console.log(err);
        }
    }
    useEffect(() => {
        if (combinedData.length > 0) {
            fetchPharmData();
        }
    }, [combinedData]);
    // ---------------- CALCULATIONS ----------------
    const totalMrp = rows.reduce((t, r) => {
        let s = 0;
        (r.enriched || []).forEach(i => {
            if (i.data) s += parseFloat(i.data.mrp || 0) * i.qty;
        });
        return t + s;
    }, 0);
    const totalPurchase = rows.reduce((t, r) => {
        let s = 0;
        (r.enriched || []).forEach(i => {
            if (i.data) s += parseFloat(i.data.pprice || 0) * i.qty;
        });
        return t + s;
    }, 0);
    const totalPaid = rows.reduce(
        (t, r) => t + parseFloat(r.paid || 0),
        0
    );
    const profit = totalMrp - totalPurchase;
    const profitPercent =
        totalPurchase > 0 ? ((profit / totalPurchase) * 100).toFixed(2) : 0;
// =====================================
// DISCOUNT
// =====================================
    const totalDiscount = rows.reduce((total, row) => {
        return total + Number(
            row.discount || 0
        );
    }, 0);
// =====================================
// NET SALES
// =====================================
    const netSales =
        totalMrp - totalDiscount;
// =====================================
// GROSS PROFIT
// =====================================
    const grossProfit =
        netSales - totalPurchase;
// =====================================
// PROFIT %
// =====================================
    const profitPercentage =
        totalPurchase > 0
            ? (
                (grossProfit / totalPurchase) * 100
            ).toFixed(2)
            : 0;
// =====================================
// GRAPH
// =====================================
    const pharmacyProfitGraph = [
        {
            name: "MRP",
            amount: totalMrp
        },
        {
            name: "Discount",
            amount: totalDiscount
        },
        {
            name: "Net Sales",
            amount: netSales
        },
        {
            name: "Purchase",
            amount: totalPurchase
        },
        {
            name: "Profit",
            amount: grossProfit
        }
    ];
    // =========================
    // AUTO REFRESH
    // =========================
    useEffect(() => {
        fetchDashboardData();
        const interval = setInterval(() => {
            fetchDashboardData();
        }, 20000);
        return () => clearInterval(interval);
    }, []);
    // =========================
    // UI
    // =========================
    return (
        <section
            className="container-fluid p-4"
            style={{
                minHeight: "100vh",
                color: "white"
            }}
        >

            {/* HEADER */}

            <div className="d-flex justify-content-between align-items-center mb-4">

                <h2 style={{fontWeight: "bold"}}>
                    Hospital Command Center
                </h2>

                <h5>
                    {new Date().toLocaleString()}
                </h5>

            </div>

            {/* KPI CARDS */}

            <div className="row">

                <DashboardCard
                    title="OP Today"
                    value={opCount}
                    color="#3b82f6"
                />

                <DashboardCard
                    title="IP Patients"
                    value={ipCount}
                    color="#ef4444"
                />

                <DashboardCard
                    title="Occupied Beds"
                    value={occupancy.occupied}
                    color="#f97316"
                />

                <DashboardCard
                    title="Available Beds"
                    value={occupancy.available}
                    color="#10b981"
                />

                <DashboardCard
                    title="ICU Occupancy"
                    value={occupancy.icu}
                    color="#f59e0b"
                />

                <DashboardCard
                    title="Lab Tests"
                    value={labCount}
                    color="#8b5cf6"
                />

                <DashboardCard
                    title="Radiology"
                    value={invCount}
                    color="#ec4899"
                />

                <DashboardCard
                    title="Revenue Today"
                    value={"₹ " + revenue}
                    color="#14b8a6"
                />

            </div>

            {/* CHARTS */}

            <div className="row mt-4">

                {/* REVENUE */}

                <div className="col-md-6 mb-4">

                    <div className="card bg-dark text-white shadow-lg">

                        <div className="card-header">
                            Revenue Distribution
                        </div>

                        <div className="card-body">

                            <ResponsiveContainer width="100%" height={300}>

                                <LineChart data={revenueTrend}>

                                    <CartesianGrid strokeDasharray="3 3"/>

                                    <XAxis dataKey="time"/>

                                    <YAxis/>

                                    <Tooltip/>

                                    <Legend/>

                                    <Line
                                        type="monotone"
                                        dataKey="amount"
                                        stroke="#00C49F"
                                        strokeWidth={3}
                                    />

                                </LineChart>

                            </ResponsiveContainer>

                        </div>

                    </div>

                </div>

                {/* WARD OCCUPANCY */}

                <div className="col-md-6 mb-4">

                    <div className="card bg-dark text-white shadow-lg">

                        <div className="card-header">
                            Ward Occupancy
                        </div>

                        <div className="card-body">

                            <ResponsiveContainer width="100%" height={300}>

                                <BarChart data={wardData}>

                                    <CartesianGrid strokeDasharray="3 3"/>

                                    <XAxis dataKey="ward"/>

                                    <YAxis/>

                                    <Tooltip/>

                                    <Bar
                                        dataKey="occupied"
                                        fill="#3b82f6"
                                    />

                                </BarChart>

                            </ResponsiveContainer>

                        </div>

                    </div>

                </div>

            </div>

            {/* BOTTOM ROW */}

            <div className="row">

                {/* PATIENT FLOW */}

                <div className="col-md-4 mb-4">

                    <div className="card bg-dark text-white shadow-lg">

                        <div className="card-header">
                            Patient Flow
                        </div>

                        <div className="card-body">

                            <ResponsiveContainer width="100%" height={300}>

                                <PieChart>

                                    <Pie
                                        data={patientFlow}
                                        dataKey="value"
                                        outerRadius={100}
                                        label
                                    >

                                        {patientFlow.map((entry, index) => (
                                            <Cell
                                                key={index}
                                                fill={COLORS[index % COLORS.length]}
                                            />
                                        ))}

                                    </Pie>

                                    <Tooltip/>

                                    <Legend/>

                                </PieChart>

                            </ResponsiveContainer>

                        </div>

                    </div>

                </div>

                {/* ALERTS */}

                <div className="col-md-4 mb-4">

                    <div className="card bg-dark text-white shadow-lg">

                        <div className="card-header">
                            Operational Alerts
                        </div>

                        <div className="card-body">

                            {alerts.length === 0 && (
                                <div className="alert alert-success">
                                    No Critical Alerts
                                </div>
                            )}

                            {alerts.map((a, index) => (
                                <div
                                    key={index}
                                    className={`alert ${
                                        a.type === "critical"
                                            ? "alert-danger"
                                            : "alert-warning"
                                    }`}
                                >

                                    {a.message}

                                </div>
                            ))}

                        </div>

                    </div>

                </div>

                {/* CRITICAL PATIENTS */}

                <div className="col-md-4 mb-4">

                    <div className="card bg-dark text-white shadow-lg">

                        <div className="card-header">
                            Critical Patients
                        </div>

                        <div className="card-body p-0">

                            <div className="table-responsive">

                                <table
                                    className="table mb-0"
                                    style={{
                                        color: "white"
                                    }}
                                >

                                    <thead
                                        style={{
                                            background: "#7f1d1d"
                                        }}
                                    >

                                    <tr>

                                        <th
                                            style={{
                                                border: "none",
                                                padding: "14px"
                                            }}
                                        >
                                            Patient
                                        </th>

                                        <th
                                            style={{
                                                border: "none",
                                                padding: "14px"
                                            }}
                                        >
                                            Ward
                                        </th>

                                        <th
                                            style={{
                                                border: "none",
                                                padding: "14px"
                                            }}
                                        >
                                            Status
                                        </th>

                                    </tr>

                                    </thead>

                                    <tbody>

                                    {criticalPatients.length === 0 && (
                                        <tr>

                                            <td
                                                colSpan="3"
                                                className="text-center"
                                                style={{
                                                    padding: "25px",
                                                    color: "#94a3b8"
                                                }}
                                            >
                                                No Critical Patients
                                            </td>

                                        </tr>
                                    )}

                                    {criticalPatients.map((p, index) => (
                                        <tr
                                            key={index}
                                            style={{
                                                background:
                                                    index % 2 === 0
                                                        ? "#111827"
                                                        : "#1e293b"
                                            }}
                                        >

                                            <td
                                                style={{
                                                    border: "none",
                                                    padding: "14px",
                                                    fontWeight: "600"
                                                }}
                                            >
                                                {p.patient}
                                            </td>

                                            <td
                                                style={{
                                                    border: "none",
                                                    padding: "14px",
                                                    color: "#fca5a5"
                                                }}
                                            >
                                                {p.ward}
                                            </td>

                                            <td
                                                style={{
                                                    border: "none",
                                                    padding: "14px"
                                                }}
                                            >

                          <span
                              style={{
                                  background: "#dc2626",
                                  color: "white",
                                  padding: "6px 12px",
                                  borderRadius: "20px",
                                  fontSize: "12px",
                                  fontWeight: "bold"
                              }}
                          >
                            {p.status}
                          </span>

                                            </td>

                                        </tr>
                                    ))}

                                    </tbody>

                                </table>

                            </div>

                        </div>

                    </div>

                </div>

                <div className="col-md-12 mt-4">

                    <div className="card shadow-lg">

                        <div
                            className="card-header"
                            style={{
                                background: "#0f172a",
                                color: "white",
                                fontWeight: "bold"
                            }}
                        >
                            Pharmacy Profit Statement
                        </div>

                        <div className="card-body">

                            <div className="row mb-4">

                                <div className="col-md-2">
                                    <h6>Total MRP</h6>
                                    <h4>₹ {totalMrp.toFixed(2)}</h4>
                                </div>

                                <div className="col-md-2">
                                    <h6>Discount</h6>
                                    <h4 style={{color: "red"}}>
                                        ₹ {totalDiscount.toFixed(2)}
                                    </h4>
                                </div>

                                <div className="col-md-2">
                                    <h6>Net Sales</h6>
                                    <h4 style={{color: "green"}}>
                                        ₹ {netSales.toFixed(2)}
                                    </h4>
                                </div>

                                <div className="col-md-2">
                                    <h6>Purchase</h6>
                                    <h4>
                                        ₹ {totalPurchase.toFixed(2)}
                                    </h4>
                                </div>

                                <div className="col-md-2">
                                    <h6>Gross Profit</h6>
                                    <h4
                                        style={{
                                            color:
                                                grossProfit > 0
                                                    ? "green"
                                                    : "red"
                                        }}
                                    >
                                        ₹ {grossProfit.toFixed(2)}
                                    </h4>
                                </div>

                                <div className="col-md-2">
                                    <h6>Profit %</h6>
                                    <h4 style={{color: "#2563eb"}}>
                                        {profitPercentage}%
                                    </h4>
                                </div>

                            </div>

                            <ResponsiveContainer
                                width="100%"
                                height={400}
                            >

                                <BarChart
                                    data={pharmacyProfitGraph}
                                >

                                    <CartesianGrid
                                        strokeDasharray="3 3"
                                    />

                                    <XAxis dataKey="name"/>

                                    <YAxis/>

                                    <Tooltip/>

                                    <Legend/>

                                    <Bar
                                        dataKey="amount"
                                        fill="#3b82f6"
                                    />

                                </BarChart>

                            </ResponsiveContainer>

                        </div>

                    </div>

                </div>
            </div>

        </section>
    );
}
// =========================
// KPI CARD
// =========================
function DashboardCard({title, value, color}) {
    return (
        <div className="col-md-3 col-lg-2 mb-3">

            <div
                style={{
                    borderRadius: "16px",
                    padding: "20px",
                    boxShadow: "0 4px 15px rgba(0,0,0,0.4)",
                    borderLeft: `6px solid ${color}`,
                    minHeight: "120px"
                }}
            >

                <h6>
                    {title}
                </h6>

                <h2
                    style={{
                        fontWeight: "bold",
                        marginTop: "15px"
                    }}
                >
                    {value}
                </h2>

            </div>

        </div>
    );
}
export default ExecutiveDashboard;